import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  SpaceBetween,
  FormField,
  Input,
  Select,
  Tabs,
  Checkbox,
  DatePicker,
  Button,
  Textarea,
  Grid,
  ColumnLayout,
  Multiselect
} from '@amzn/awsui-components-react';
import './SchedulerSetupModal.scss';

const SchedulerSetupModal = ({
  visible,
  onDismiss,
  onSave,
  initialData = null,
  datasets = [],
  applications = [],
  mode = 'add' // 'add', 'edit', or 'view'
}) => {
  // Form state
  const [schedulerName, setSchedulerName] = useState('');
  const [datasetUrl, setDatasetUrl] = useState(null);
  const [selectedFrequency, setSelectedFrequency] = useState('hourly');
  const [beginScheduleOn, setBeginScheduleOn] = useState('');
  const [selectedApplications, setSelectedApplications] = useState([]);

  // Hourly state
  const [hourlyWorkingDay, setHourlyWorkingDay] = useState(true);
  const [hourlyWorkingDayBegin, setHourlyWorkingDayBegin] = useState({ label: 'WD-1', value: 'WD-1' });
  const [hourlyWorkingDayEnd, setHourlyWorkingDayEnd] = useState({ label: 'WD23', value: 'WD23' });
  const [hourlyCalendarDay, setHourlyCalendarDay] = useState(false);
  const [hourlyCalendarDayBegin, setHourlyCalendarDayBegin] = useState({ label: '-1', value: '-1' });
  const [hourlyCalendarDayEnd, setHourlyCalendarDayEnd] = useState({ label: '31', value: '31' });

  // Daily state
  const [dailyWorkingDay, setDailyWorkingDay] = useState(true);
  const [dailyWorkingDayBegin, setDailyWorkingDayBegin] = useState({ label: 'WD-1', value: 'WD-1' });
  const [dailyWorkingDayEnd, setDailyWorkingDayEnd] = useState({ label: 'WD23', value: 'WD23' });
  const [dailyCalendarDay, setDailyCalendarDay] = useState(false);
  const [dailyCalendarDayBegin, setDailyCalendarDayBegin] = useState({ label: '-1', value: '-1' });
  const [dailyCalendarDayEnd, setDailyCalendarDayEnd] = useState({ label: '31', value: '31' });
  const [dailyEveryXDays, setDailyEveryXDays] = useState(false);
  const [dailyEveryXDaysValue, setDailyEveryXDaysValue] = useState('2');

  // Weekly state
  const [weeklyEveryXWeeks, setWeeklyEveryXWeeks] = useState('2');
  const [weeklyMonday, setWeeklyMonday] = useState(true);
  const [weeklyTuesday, setWeeklyTuesday] = useState(true);
  const [weeklyWednesday, setWeeklyWednesday] = useState(true);
  const [weeklyThursday, setWeeklyThursday] = useState(true);
  const [weeklyFriday, setWeeklyFriday] = useState(true);
  const [weeklySaturday, setWeeklySaturday] = useState(false);
  const [weeklySunday, setWeeklySunday] = useState(false);

  // Monthly state
  const [monthlyWorkingDay, setMonthlyWorkingDay] = useState(true);
  const [monthlyWorkingDayValue, setMonthlyWorkingDayValue] = useState('2');
  const [monthlyWorkingDayMonths, setMonthlyWorkingDayMonths] = useState('3');
  const [monthlyCalendarDay, setMonthlyCalendarDay] = useState(false);
  const [monthlyCalendarDayValue, setMonthlyCalendarDayValue] = useState('1');
  const [monthlyCalendarDayMonths, setMonthlyCalendarDayMonths] = useState('1');

  // Yearly state
  const [yearlyEveryXYears, setYearlyEveryXYears] = useState('1');
  const [yearlyOptionDate, setYearlyOptionDate] = useState(true);
  const [yearlyDateValue, setYearlyDateValue] = useState('');
  const [yearlyOptionWorkingDay, setYearlyOptionWorkingDay] = useState(false);
  const [yearlyWorkingDayValue, setYearlyWorkingDayValue] = useState({ label: 'WD1', value: 'WD1' });
  const [yearlyWorkingDayMonth, setYearlyWorkingDayMonth] = useState({ label: 'Oct', value: 'Oct' });
  const [yearlyOptionCalendarDay, setYearlyOptionCalendarDay] = useState(false);
  const [yearlyCalendarDayValue, setYearlyCalendarDayValue] = useState('1');
  const [yearlyCalendarDayMonth, setYearlyCalendarDayMonth] = useState({ label: 'Oct', value: 'Oct' });

  // Cron state
  const [cronExpression, setCronExpression] = useState('');

  // Validation errors
  const [errors, setErrors] = useState({});

  // Generate working day options (WD-1 to WD23)
  const generateWorkingDayOptions = () => {
    const options = [];
    options.push({ label: 'WD-1', value: 'WD-1' });
    for (let i = 1; i <= 23; i++) {
      options.push({ label: `WD${i}`, value: `WD${i}` });
    }
    return options;
  };

  // Generate calendar day options (-1 to 31)
  const generateCalendarDayOptions = () => {
    const options = [];
    options.push({ label: '-1', value: '-1' });
    for (let i = 1; i <= 31; i++) {
      options.push({ label: String(i), value: String(i) });
    }
    return options;
  };

  // Generate month options
  const monthOptions = [
    { label: 'Jan', value: 'Jan' },
    { label: 'Feb', value: 'Feb' },
    { label: 'Mar', value: 'Mar' },
    { label: 'Apr', value: 'Apr' },
    { label: 'May', value: 'May' },
    { label: 'Jun', value: 'Jun' },
    { label: 'Jul', value: 'Jul' },
    { label: 'Aug', value: 'Aug' },
    { label: 'Sep', value: 'Sep' },
    { label: 'Oct', value: 'Oct' },
    { label: 'Nov', value: 'Nov' },
    { label: 'Dec', value: 'Dec' }
  ];

  const workingDayOptions = generateWorkingDayOptions();
  const calendarDayOptions = generateCalendarDayOptions();

  // Initialize form with initial data if in edit mode
  useEffect(() => {
    if (initialData && mode === 'edit') {
      setSchedulerName(initialData.schedulerName || '');
      setDatasetUrl(initialData.datasetUrl || null);
      setBeginScheduleOn(initialData.beginScheduleOn || '');
      setSelectedApplications(initialData.applications || []);
      // Add more field initializations as needed
    } else if (mode === 'add') {
      // Reset form for add mode
      resetForm();
    }
  }, [initialData, mode, visible]);

  const resetForm = () => {
    setSchedulerName('');
    setDatasetUrl(null);
    setSelectedFrequency('hourly');
    setBeginScheduleOn('');
    setSelectedApplications([]);
    setErrors({});
    // Reset all frequency-specific fields to defaults
    setHourlyWorkingDay(true);
    setHourlyCalendarDay(false);
    setDailyWorkingDay(true);
    setDailyCalendarDay(false);
    setDailyEveryXDays(false);
    setWeeklyMonday(true);
    setWeeklyTuesday(true);
    setWeeklyWednesday(true);
    setWeeklyThursday(true);
    setWeeklyFriday(true);
    setWeeklySaturday(false);
    setWeeklySunday(false);
    setMonthlyWorkingDay(true);
    setMonthlyCalendarDay(false);
    setYearlyOptionDate(true);
    setYearlyOptionWorkingDay(false);
    setYearlyOptionCalendarDay(false);
    setCronExpression('');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!schedulerName.trim()) {
      newErrors.schedulerName = 'Scheduler name is required';
    }

    if (!datasetUrl) {
      newErrors.datasetUrl = 'Dataset URL is required';
    }

    if (!beginScheduleOn) {
      newErrors.beginScheduleOn = 'Begin Schedule On date is required';
    }

    if (selectedFrequency === 'cron' && !cronExpression.trim()) {
      newErrors.cronExpression = 'Cron expression is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const formData = {
      schedulerName,
      datasetUrl: datasetUrl?.value,
      frequency: selectedFrequency,
      beginScheduleOn,
      applications: selectedApplications,
      frequencyConfig: getFrequencyConfig()
    };

    onSave(formData);
  };

  const getFrequencyConfig = () => {
    switch (selectedFrequency) {
      case 'hourly':
        return {
          workingDay: hourlyWorkingDay,
          workingDayBegin: hourlyWorkingDayBegin?.value,
          workingDayEnd: hourlyWorkingDayEnd?.value,
          calendarDay: hourlyCalendarDay,
          calendarDayBegin: hourlyCalendarDayBegin?.value,
          calendarDayEnd: hourlyCalendarDayEnd?.value
        };
      case 'daily':
        return {
          workingDay: dailyWorkingDay,
          workingDayBegin: dailyWorkingDayBegin?.value,
          workingDayEnd: dailyWorkingDayEnd?.value,
          calendarDay: dailyCalendarDay,
          calendarDayBegin: dailyCalendarDayBegin?.value,
          calendarDayEnd: dailyCalendarDayEnd?.value,
          everyXDays: dailyEveryXDays,
          everyXDaysValue: dailyEveryXDaysValue
        };
      case 'weekly':
        return {
          everyXWeeks: weeklyEveryXWeeks,
          days: {
            monday: weeklyMonday,
            tuesday: weeklyTuesday,
            wednesday: weeklyWednesday,
            thursday: weeklyThursday,
            friday: weeklyFriday,
            saturday: weeklySaturday,
            sunday: weeklySunday
          }
        };
      case 'monthly':
        return {
          workingDay: monthlyWorkingDay,
          workingDayValue: monthlyWorkingDayValue,
          workingDayMonths: monthlyWorkingDayMonths,
          calendarDay: monthlyCalendarDay,
          calendarDayValue: monthlyCalendarDayValue,
          calendarDayMonths: monthlyCalendarDayMonths
        };
      case 'yearly':
        return {
          everyXYears: yearlyEveryXYears,
          optionDate: yearlyOptionDate,
          dateValue: yearlyDateValue,
          optionWorkingDay: yearlyOptionWorkingDay,
          workingDayValue: yearlyWorkingDayValue?.value,
          workingDayMonth: yearlyWorkingDayMonth?.value,
          optionCalendarDay: yearlyOptionCalendarDay,
          calendarDayValue: yearlyCalendarDayValue,
          calendarDayMonth: yearlyCalendarDayMonth?.value
        };
      case 'cron':
        return {
          expression: cronExpression
        };
      default:
        return {};
    }
  };

  const isViewMode = mode === 'view';

  const frequencyTabs = [
    {
      label: 'Hourly',
      id: 'hourly',
      content: (
        <SpaceBetween size="m">
          <Box>
            <Checkbox
              checked={hourlyWorkingDay}
              onChange={({ detail }) => setHourlyWorkingDay(detail.checked)}
              disabled={isViewMode}
            >
               Working day
            </Checkbox>
            <Box margin={{ left: 'xxl', top: 'xs' }}>
              <Grid gridDefinition={[{ colspan: 2 }, { colspan: 3 }, { colspan: 2 }, { colspan: 3 }]}>
                <Box padding={{ top: 'xs' }}>Begin On</Box>
                <Select
                  selectedOption={hourlyWorkingDayBegin}
                  onChange={({ detail }) => setHourlyWorkingDayBegin(detail.selectedOption)}
                  options={workingDayOptions}
                  disabled={isViewMode}
                />
                <Box padding={{ top: 'xs' }}>End On</Box>
                <Select
                  selectedOption={hourlyWorkingDayEnd}
                  onChange={({ detail }) => setHourlyWorkingDayEnd(detail.selectedOption)}
                  options={workingDayOptions}
                  disabled={isViewMode}
                />
              </Grid>
            </Box>
          </Box>

          <Box>
            <Checkbox
              checked={hourlyCalendarDay}
              onChange={({ detail }) => setHourlyCalendarDay(detail.checked)}
              disabled={isViewMode}
            >
                Calendar day
            </Checkbox>
            <Box margin={{ left: 'xxl', top: 'xs' }}>
              <Grid gridDefinition={[{ colspan: 2 }, { colspan: 3 }, { colspan: 2 }, { colspan: 3 }]}>
                <Box padding={{ top: 'xs' }}>Begin On</Box>
                <Select
                  selectedOption={hourlyCalendarDayBegin}
                  onChange={({ detail }) => setHourlyCalendarDayBegin(detail.selectedOption)}
                  options={calendarDayOptions}
                  disabled={isViewMode}
                />
                <Box padding={{ top: 'xs' }}>End On</Box>
                <Select
                  selectedOption={hourlyCalendarDayEnd}
                  onChange={({ detail }) => setHourlyCalendarDayEnd(detail.selectedOption)}
                  options={calendarDayOptions}
                  disabled={isViewMode}
                />
              </Grid>
            </Box>
          </Box>
        </SpaceBetween>
      )
    },
    {
      label: 'Daily',
      id: 'daily',
      content: (
        <SpaceBetween size="m">
          <Box>
            <Checkbox
              checked={dailyWorkingDay}
              onChange={({ detail }) => setDailyWorkingDay(detail.checked)}
              disabled={isViewMode}
            >
              Working day
            </Checkbox>
            <Box margin={{ left: 'xxl', top: 'xs' }}>
              <Grid gridDefinition={[{ colspan: 2 }, { colspan: 3 }, { colspan: 2 }, { colspan: 3 }]}>
                <Box padding={{ top: 'xs' }}>Begin On</Box>
                <Select
                  selectedOption={dailyWorkingDayBegin}
                  onChange={({ detail }) => setDailyWorkingDayBegin(detail.selectedOption)}
                  options={workingDayOptions}
                  disabled={isViewMode}
                />
                <Box padding={{ top: 'xs' }}>End On</Box>
                <Select
                  selectedOption={dailyWorkingDayEnd}
                  onChange={({ detail }) => setDailyWorkingDayEnd(detail.selectedOption)}
                  options={workingDayOptions}
                  disabled={isViewMode}
                />
              </Grid>
            </Box>
          </Box>

          <Box>
            <Checkbox
              checked={dailyCalendarDay}
              onChange={({ detail }) => setDailyCalendarDay(detail.checked)}
              disabled={isViewMode}
            >
              Calendar day
            </Checkbox>
            <Box margin={{ left: 'xxl', top: 'xs' }}>
              <Grid gridDefinition={[{ colspan: 2 }, { colspan: 3 }, { colspan: 2 }, { colspan: 3 }]}>
                <Box padding={{ top: 'xs' }}>Begin On</Box>
                <Select
                  selectedOption={dailyCalendarDayBegin}
                  onChange={({ detail }) => setDailyCalendarDayBegin(detail.selectedOption)}
                  options={calendarDayOptions}
                  disabled={isViewMode}
                />
                <Box padding={{ top: 'xs' }}>End On</Box>
                <Select
                  selectedOption={dailyCalendarDayEnd}
                  onChange={({ detail }) => setDailyCalendarDayEnd(detail.selectedOption)}
                  options={calendarDayOptions}
                  disabled={isViewMode}
                />
              </Grid>
            </Box>
          </Box>

          <Grid gridDefinition={[{ colspan: 2 }, { colspan: 2 }, { colspan: 8 }]}>
            <Checkbox
              checked={dailyEveryXDays}
              onChange={({ detail }) => setDailyEveryXDays(detail.checked)}
              disabled={isViewMode}
            >
              Every
            </Checkbox>
            <Select
              selectedOption={{ label: dailyEveryXDaysValue, value: dailyEveryXDaysValue }}
              onChange={({ detail }) => setDailyEveryXDaysValue(detail.selectedOption.value)}
              options={[...Array(30)].map((_, i) => ({ label: String(i + 1), value: String(i + 1) }))}
              disabled={isViewMode}
            />
            <Box padding={{ top: 'xs' }}>days</Box>
          </Grid>
        </SpaceBetween>
      )
    },
    {
      label: 'Weekly',
      id: 'weekly',
      content: (
        <SpaceBetween size="m">
          <Grid gridDefinition={[{ colspan: 2 }, { colspan: 2 }, { colspan: 8 }]}>
            <Box padding={{ top: 'xs' }}>Every</Box>
            <Select
              selectedOption={{ label: weeklyEveryXWeeks, value: weeklyEveryXWeeks }}
              onChange={({ detail }) => setWeeklyEveryXWeeks(detail.selectedOption.value)}
              options={[...Array(52)].map((_, i) => ({ label: String(i + 1), value: String(i + 1) }))}
              disabled={isViewMode}
            />
            <Box padding={{ top: 'xs' }}>week(s) on</Box>
          </Grid>

          <SpaceBetween size="m">
            {/* First row - Monday to Thursday */}
            <Grid gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}>
              <Checkbox
                checked={weeklyMonday}
                onChange={({ detail }) => setWeeklyMonday(detail.checked)}
                disabled={isViewMode}
              >
               Monday
              </Checkbox>
              <Checkbox
                checked={weeklyTuesday}
                onChange={({ detail }) => setWeeklyTuesday(detail.checked)}
                disabled={isViewMode}
              >
               Tuesday
              </Checkbox>
              <Checkbox
                checked={weeklyWednesday}
                onChange={({ detail }) => setWeeklyWednesday(detail.checked)}
                disabled={isViewMode}
              >
               Wednesday
              </Checkbox>
              <Checkbox
                checked={weeklyThursday}
                onChange={({ detail }) => setWeeklyThursday(detail.checked)}
                disabled={isViewMode}
              >
              Thursday
              </Checkbox>
            </Grid>
            
            {/* Second row - Friday to Sunday */}
            <Grid gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}>
              <Checkbox
                checked={weeklyFriday}
                onChange={({ detail }) => setWeeklyFriday(detail.checked)}
                disabled={isViewMode}
              >
               Friday
              </Checkbox>
              <Checkbox
                checked={weeklySaturday}
                onChange={({ detail }) => setWeeklySaturday(detail.checked)}
                disabled={isViewMode}
              >
               Saturday
              </Checkbox>
              <Checkbox
                checked={weeklySunday}
                onChange={({ detail }) => setWeeklySunday(detail.checked)}
                disabled={isViewMode}
              >
               Sunday
              </Checkbox>
              <Box />
            </Grid>
          </SpaceBetween>
        </SpaceBetween>
      )
    },
    {
      label: 'Monthly',
      id: 'monthly',
      content: (
        <SpaceBetween size="m">
          <Grid gridDefinition={[{ colspan: 3 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 3 }]}>
            <Box padding={{ top: 'xs' }}>
              <Checkbox
                checked={monthlyWorkingDay}
                onChange={({ detail }) => setMonthlyWorkingDay(detail.checked)}
                disabled={isViewMode}
              >
               Working day
              </Checkbox>
            </Box>
            { (
              <>
                <Select
                  selectedOption={{ label: monthlyWorkingDayValue, value: monthlyWorkingDayValue }}
                  onChange={({ detail }) => setMonthlyWorkingDayValue(detail.selectedOption.value)}
                  options={[...Array(31)].map((_, i) => ({ label: String(i + 1), value: String(i + 1) }))}
                  disabled={isViewMode}
                />
                <Box padding={{ top: 'xs' }}>of every</Box>
                <Select
                  selectedOption={{ label: monthlyWorkingDayMonths, value: monthlyWorkingDayMonths }}
                  onChange={({ detail }) => setMonthlyWorkingDayMonths(detail.selectedOption.value)}
                  options={[...Array(12)].map((_, i) => ({ label: String(i + 1), value: String(i + 1) }))}
                  disabled={isViewMode}
                />
                <Box padding={{ top: 'xs' }}>months</Box>
              </>
            )}
          </Grid>

          <Grid gridDefinition={[{ colspan: 3 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 3 }]}>
            <Box padding={{ top: 'xs' }}>
              <Checkbox
                checked={monthlyCalendarDay}
                onChange={({ detail }) => setMonthlyCalendarDay(detail.checked)}
                disabled={isViewMode}
              >
                Calendar day
              </Checkbox>
            </Box>
            {(
              <>
                <Select
                  selectedOption={{ label: monthlyCalendarDayValue, value: monthlyCalendarDayValue }}
                  onChange={({ detail }) => setMonthlyCalendarDayValue(detail.selectedOption.value)}
                  options={[...Array(31)].map((_, i) => ({ label: String(i + 1), value: String(i + 1) }))}
                  disabled={isViewMode}
                />
                <Box padding={{ top: 'xs' }}>of every</Box>
                <Select
                  selectedOption={{ label: monthlyCalendarDayMonths, value: monthlyCalendarDayMonths }}
                  onChange={({ detail }) => setMonthlyCalendarDayMonths(detail.selectedOption.value)}
                  options={[...Array(12)].map((_, i) => ({ label: String(i + 1), value: String(i + 1) }))}
                  disabled={isViewMode}
                />
                <Box padding={{ top: 'xs' }}>months</Box>
              </>
            )}
          </Grid>
        </SpaceBetween>
      )
    },
    {
      label: 'Yearly',
      id: 'yearly',
      content: (
        <SpaceBetween size="m">
          <Box>
            <Grid gridDefinition={[{ colspan: 3 }, { colspan: 2 }, { colspan: 7}]}>
              <Box padding={{ top: 'xs' }}>Every</Box>
              <Select
                selectedOption={{ label: yearlyEveryXYears, value: yearlyEveryXYears }}
                onChange={({ detail }) => setYearlyEveryXYears(detail.selectedOption.value)}
                options={[...Array(10)].map((_, i) => ({ label: String(i + 1), value: String(i + 1) }))}
                disabled={isViewMode}
              />
              <Box padding={{ top: 'xs' }}>year(s)</Box>
            </Grid>
          </Box>

          <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
            <Box padding={{ top: 'xs' }}>
              <Checkbox
                checked={yearlyOptionDate}
                onChange={({ detail }) => {
                  setYearlyOptionDate(detail.checked);
                  if (detail.checked) {
                    setYearlyOptionWorkingDay(false);
                    setYearlyOptionCalendarDay(false);
                  }
                }}
                disabled={isViewMode}
              >
               On
              </Checkbox>
            </Box>
            { (
              <DatePicker
                value={yearlyDateValue}
                onChange={({ detail }) => setYearlyDateValue(detail.value)}
                placeholder="Date"
                disabled={isViewMode}
              />
            )}
          </Grid>

          <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
            <Box padding={{ top: 'xs' }}>
              <Checkbox
                checked={yearlyOptionWorkingDay}
                onChange={({ detail }) => {
                  setYearlyOptionWorkingDay(detail.checked);
                  if (detail.checked) {
                    setYearlyOptionDate(false);
                    setYearlyOptionCalendarDay(false);
                  }
                }}
                disabled={isViewMode}
              >
               On
              </Checkbox>
            </Box>
            { (
              <Grid gridDefinition={[{ colspan: 3 }, { colspan: 1 }, { colspan: 3 }]}>
                <Select
                  selectedOption={yearlyWorkingDayValue}
                  onChange={({ detail }) => setYearlyWorkingDayValue(detail.selectedOption)}
                  options={workingDayOptions}
                  disabled={isViewMode}
                />
                <Box padding={{ top: 'xs' }}>of</Box>
                <Select
                  selectedOption={yearlyWorkingDayMonth}
                  onChange={({ detail }) => setYearlyWorkingDayMonth(detail.selectedOption)}
                  options={monthOptions}
                  disabled={isViewMode}
                />
              </Grid>
            )}
          </Grid>

          <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
            <Box padding={{ top: 'xs' }}>
              <Checkbox
                checked={yearlyOptionCalendarDay}
                onChange={({ detail }) => {
                  setYearlyOptionCalendarDay(detail.checked);
                  if (detail.checked) {
                    setYearlyOptionDate(false);
                    setYearlyOptionWorkingDay(false);
                  }
                }}
                disabled={isViewMode}
              >
               On calendar day
              </Checkbox>
            </Box>
            { (
              <Grid gridDefinition={[{ colspan: 3 }, { colspan: 1 }, { colspan: 3 }]}>
                <Select
                  selectedOption={{ label: yearlyCalendarDayValue, value: yearlyCalendarDayValue }}
                  onChange={({ detail }) => setYearlyCalendarDayValue(detail.selectedOption.value)}
                  options={[...Array(31)].map((_, i) => ({ label: String(i + 1), value: String(i + 1) }))}
                  disabled={isViewMode}
                />
                <Box padding={{ top: 'xs' }}>of</Box>
                <Select
                  selectedOption={yearlyCalendarDayMonth}
                  onChange={({ detail }) => setYearlyCalendarDayMonth(detail.selectedOption)}
                  options={monthOptions}
                  disabled={isViewMode}
                />
              </Grid>
            )}
          </Grid>
        </SpaceBetween>
      )
    },
    {
      label: 'Cron',
      id: 'cron',
      content: (
        <FormField
          label=""
          errorText={errors.cronExpression}
        >
          <Textarea
            value={cronExpression}
            onChange={({ detail }) => setCronExpression(detail.value)}
            placeholder="Paste your Cron expression here"
            rows={6}
            disabled={isViewMode}
          />
        </FormField>
      )
    }
  ];

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={
        mode === 'view' 
          ? `${initialData?.schedulerName || 'Scheduler'} (View only)`
          : (
            <Box className="modal-header-container">
              <Box 
                fontSize="heading-xl" 
                fontWeight="bold"
              >
                Scheduler Setup
              </Box>
              <Box className="modal-header-divider" />
            </Box>
          )
      }
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button 
              variant="link" 
              onClick={onDismiss}
            >
              Cancel
            </Button>
            {!isViewMode && (
              <Button 
                variant="primary" 
                onClick={handleSave}
              >
                {mode === 'edit' ? 'Save' : 'Schedule'}
              </Button>
            )}
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
          <Box padding={{ top: 'xs' }}>
            <strong>Scheduler name<Box variant="span" color="text-status-error">*</Box></strong>
          </Box>
          <FormField errorText={errors.schedulerName}>
            <Input
              value={schedulerName}
              onChange={({ detail }) => setSchedulerName(detail.value)}
              placeholder="Inset scheduler name"
              disabled={isViewMode}
            />
          </FormField>
        </Grid>

        <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
          <Box padding={{ top: 'xs' }}>
            <strong>Dataset URL<Box variant="span" color="text-status-error">*</Box></strong>
          </Box>
          <FormField errorText={errors.datasetUrl}>
            <Select
              selectedOption={datasetUrl}
              onChange={({ detail }) => setDatasetUrl(detail.selectedOption)}
              options={datasets}
              placeholder="Dropdown with list of datasets linked to the App via Meta"
              disabled={isViewMode}
            />
          </FormField>
        </Grid>

        <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
          <Box padding={{ top: 'xs' }}>
            <strong>Frequency<Box variant="span" color="text-status-error">*</Box></strong>
          </Box>
          <Tabs
            activeTabId={selectedFrequency}
            onChange={({ detail }) => setSelectedFrequency(detail.activeTabId)}
            tabs={frequencyTabs}
            variant="default"
            className="scheduler-tabs"
          />
        </Grid>

        <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
          <Box padding={{ top: 'xs' }}>
            <strong>Begin Schedule On<Box variant="span" color="text-status-error">*</Box></strong>
          </Box>
          <FormField errorText={errors.beginScheduleOn}>
            <DatePicker
              value={beginScheduleOn}
              onChange={({ detail }) => setBeginScheduleOn(detail.value)}
              placeholder="Date"
              disabled={isViewMode}
            />
          </FormField>
        </Grid>

        <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
          <Box padding={{ top: 'xs' }}>
            <strong>Add to Another Accounting Central Application</strong>
          </Box>
          <Multiselect
            selectedOptions={selectedApplications}
            onChange={({ detail }) => setSelectedApplications(detail.selectedOptions)}
            options={applications}
            placeholder="Other Apps that have the same dataset in Meta"
            disabled={isViewMode}
          />
        </Grid>
      </SpaceBetween>
    </Modal>
  );
};

export default SchedulerSetupModal;
